import { Alert, AlertTitle, Box, Button, Card, Grid, GridProps, Pagination, Popover, Stack, Typography } from '@mui/material';
import { AccountTree } from '@mui/icons-material';
import React from 'react';
import { useAppSelector } from '../appHooks';
import CutterDetailsHistoryNetwork from '../bookmarks/factory/CutterDetailsHistory.network';

import { SearchParameters, maximalServerResultsLength, setFavorite, useGetCutterByStringQueryState } from '../redux/cutterSearchAPI';
import store, { RootState } from '../store';
import { CutterState, cutterStateColumns } from "../types/cutterState";
import CutterCard from './CutterCard';
import ExportExcel from '../common/ExportExcel';
import { Contexte } from '@lectra/react-authentication';
import ButtonMailto from '../common/ButtonMailto';


export interface CutterCardListProps extends GridProps {
    searchStr: string;
}

const CutterCardList: React.FC<CutterCardListProps> = (props: CutterCardListProps) => {
    const { searchStr, ...others } = props;
    const [expand, setexpand] = React.useState(false);
    /* Following is used ton sync Carousel between cutter cards */
    const [activeslide, setactiveslide] = React.useState(0);
    const [page, setPage] = React.useState(1); // Note that the Pagination page prop starts at 1 to match the requirement of including the value in the URL
    const [networkAnchor, setNetworkAnchor] = React.useState<HTMLButtonElement | null>(null);

    const searchParams: SearchParameters = useAppSelector((state: RootState) => state.persistedSearchReducer);
    const searchQuery: ReturnType<typeof useGetCutterByStringQueryState> = useGetCutterByStringQueryState(searchStr);

    const authUser: Contexte = useAppSelector<Contexte>((state: RootState) => state.userStateReducer);

    if (!searchQuery || searchQuery.status === 'uninitialized') {
        // Reset tab to 0 to prevent empty result display
        // if (activeSlide!==0)setActiveSlide(0);
        return (<></>);
    };

    if (searchQuery?.status === 'pending') {
        // Reset tab to 0 to prevent empty result display
        if (page > 1) setPage(1);
        // if (activeSlide!==0)setActiveSlide(0);
        return (<></>);
    };

    /*      SEARCH RESULTS : Error on load   */
    if (searchQuery?.isError) {
        // Force user to login again when Http 401 occurs
        if (searchQuery?.error?.includes('401') ?? false) { authUser.actions.logout(); window.location.reload(); }
        return (
            <Alert severity="error" sx={{ display: 'flex', justifySelf: 'center', flexDirection: 'row' }}>
                <strong>{searchQuery.error}</strong>
            </Alert>
        )
    }

    const pageSize: number = 15;
    const pageCount: number = Math.ceil((searchQuery?.data?.item2?.length ?? 0) / pageSize);
    let resultsCount: string = ((searchQuery?.data?.item1 ?? 0) >= maximalServerResultsLength) ? ` of ${searchQuery?.data?.item1?.toString()}` : '';
    resultsCount = `${resultsCount} for '${searchQuery.originalArgs ?? (searchParams.recentSearches?.[0] ?? '')}'`

    let slices: CutterState[] = [...searchQuery?.data?.item2]; // Clone data or empty
    const startIndex: number = (page - 1) * pageSize;
    slices = slices.slice(startIndex, startIndex + pageSize)

    // Detect unique company across ALL results (not just current page) : If only one unique companyId is present, 
    // we can propose a network overview for this company
    const allResults: CutterState[] = searchQuery?.data?.item2 ?? [];
    const companyIds = new Set(allResults.map(c => c.cusT_CompanyId).filter(Boolean));
    const uniqueCompanyId: string | null = companyIds.size === 1 ? Array.from(companyIds)[0] : null;
    // searchQuery is fulfilled and has no error


    /*      SEARCH RESULTS : NO RESULT   */
    if (slices.length === 0) {
        return (
            <Box component={Alert} severity="warning" sx={{ width: '100%', justifySelf: 'center', flexDirection: 'row' }} >
                <AlertTitle sx={{ justifySelf: 'center' }} >
                    <>
                        <b>No results found:</b>
                        <ButtonMailto label={`Missing data for '${searchParams.recentSearches?.[0] ?? ''}'?`}
                            subject={`[EvMConn] Looking for '${searchParams.recentSearches?.[0] ?? ''}' leads to 'No results'`}
                            recipient="DL_RD.Produce.CfactoryDev@lectra.com"
                            content={
                                `Hello,

I searched for '${searchParams.recentSearches?.[0] ?? ''}' in EvMConn and found no results.
Could you please check if this data exists in your database?
Thank you.

Best regards,

${authUser.user?.email}`} />
                    </>
                </AlertTitle>
            </Box>
        )
    }

    /*      SEARCH RESULTS : AT LEAST ONE RESULT  */
    return (
        <>
            <Stack component={Stack} direction='row' sx={{ justifyContent: 'space-between', paddingX: 0, paddingTop: '1em' }} >
                <Pagination count={pageCount} size="small" color='secondary' page={page} onChange={(_, page) => setPage(page)} sx={{ display: (pageCount > 0 ? 'flex-start' : 'none') }} />

                {uniqueCompanyId && (
                    <>
                        <Button
                            size="small"
                            variant="text"
                            color="secondary"
                            startIcon={<AccountTree fontSize="small" />}
                            onClick={(e) => setNetworkAnchor(e.currentTarget)}
                            sx={{ textTransform: 'none', fontSize: '0.78rem' }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Typography noWrap sx={{ maxWidth: '60ch', fontSize: 'inherit' }}>
                                    {allResults[0]?.cusT_CompanyName}
                                </Typography>
                                ({allResults[0]?.cusT_PlantName} {uniqueCompanyId}) overview
                            </Box>
                        </Button>
                        <Popover
                            open={Boolean(networkAnchor)}
                            onClose={() => setNetworkAnchor(null)}
                            anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                            transformOrigin={{ vertical: 'top', horizontal: 'center' }}
                            slotProps={{ paper: { sx: { width: '50vw', height: '50vw', p: 1 } } }}
                        >
                            <Typography variant="subtitle2" sx={{ px: 1, pt: 0.5, pb: 0.5, color: 'text.secondary' }}>
                               Network <b>{allResults[0]?.cusT_CompanyName} ({allResults[0]?.cusT_PlantName} {uniqueCompanyId})</b> — (Preview)
                            </Typography>
                            <CutterDetailsHistoryNetwork serialNumber={allResults[0]?.serialNumber} width='100%' height='calc(50vw - 3rem)' />
                        </Popover>
                    </>
                )}

                <Box component={Stack} direction='row' alignItems='center' sx={{ display: (pageCount > 0 ? 'flex-end' : 'none') }} >
                    &nbsp;
                    <Typography component="div" color='secondary' sx={{ fontWeight: 'bold', fontSize: { md: '0.8rem', xs: '0.6rem' } }}> {/* results?.data?.item1.toString() */}
                        {(searchQuery?.data?.item2.length ?? 0)} results {resultsCount}
                    </Typography>
                    <ExportExcel label={searchQuery.originalArgs} svg={{ width: '1.3rem' }}
                        total={searchQuery?.data?.item1}
                        results={searchQuery?.data?.item2} columns={cutterStateColumns}
                        title={`Export ${searchQuery?.data?.item2.length} '${searchQuery.originalArgs}' results`} />
                </Box>

            </Stack>

            <Grid container {...others} spacing={0.1}  >
                {slices.map((cutter: CutterState) => {
                    return (
                        <Grid key={cutter.serialNumber} size={{ md: 3.90, xs: 5.5 }}>
                            <CutterCard component={Card} {...props} cutter={cutter}
                                expandedstate={expand}
                                setexpand={() => setexpand(!expand)}
                                favoritestate={searchParams.favorites.includes(cutter.serialNumber)}
                                setfavorite={() => store.dispatch(setFavorite(cutter.serialNumber))}
                                activeslide={activeslide} setactiveslide={setactiveslide} />
                        </Grid>
                    );
                })}
            </Grid>
        </>
    )
};

export default CutterCardList;

